import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import LincNav from "./LincNav";
import GlobalPartner from "./GlobalPartner";
import { AvatarOverlayLayer } from "../features/avatarOverlay";
import EditModeHost from "../features/editMode/EditModeHost";
import { ARTI_ASK_EVENT, ARTI_TALK_EVENT } from "../lib/artiDesk";
import "./LinkWorksShell.css";
import "./LinkWorksCardDeck.css";
import "./LinkWorksOverlayFix.css";

const THEME_KEY = "linkworks.theme";

function isLincWerksHome(pathname) {
  return pathname === "/focus" || pathname === "/focus/redesign";
}

function resolveAvatarModule(pathname) {
  if (pathname.startsWith("/linkforge") || pathname.startsWith("/lincforge")) return "lincforge";
  if (pathname.startsWith("/commandlinc/buildlinc")) return "buildlinc";
  if (isLincWerksHome(pathname)) return "founder";
  return pathname.split("/").filter(Boolean)[0] || "focus";
}

export default function AppShell() {
  const { user, scope, ready } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [businesses, setBusinesses] = useState([]);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || "dark"; }
    catch { return "dark"; }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* storage unavailable */ }
  }, [theme]);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate("/login", { replace: true, state: { from: location.pathname } });
      return;
    }
    if (scope === "reset_only" && location.pathname !== "/reset-password") {
      navigate("/reset-password", { replace: true });
    }
  }, [ready, user, scope, location.pathname, navigate]);

  useEffect(() => {
    let alive = true;
    if (!user || scope !== "full") return;

    const tick = async () => {
      try {
        const [{ data: summary }, { data: businessRows }] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/businesses"),
        ]);
        if (!alive) return;
        setPending(summary?.kpis?.pending_approvals || 0);
        setBusinesses(Array.isArray(businessRows) ? businessRows : []);
      } catch {
        /* Keep the shell usable if one aggregate endpoint is temporarily unavailable. */
      }
    };

    tick();
    const iv = setInterval(tick, 30000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [user, scope, location.pathname]);

  const lincWerksHome = isLincWerksHome(location.pathname);
  const openGlobalPartner = () => {
    const launcher = document.querySelector(".global-partner-launch");
    if (launcher && !launcher.classList.contains("open")) launcher.click();
  };

  const onAvatarAsk = (avatarId) => {
    if (avatarId === "arti") {
      window.dispatchEvent(new CustomEvent(ARTI_ASK_EVENT));
      return;
    }
    openGlobalPartner();
  };

  const onAvatarVoiceToggle = (avatarId) => {
    if (avatarId === "arti") {
      window.dispatchEvent(new CustomEvent(ARTI_TALK_EVENT));
      return;
    }
    openGlobalPartner();
  };

  if (!ready || !user || scope === "reset_only") return null;

  return (
    <div className="app-shell" data-testid="app-shell" data-edit-name="App shell" data-avatar-runtime="true" data-lw-redesign={lincWerksHome ? "true" : undefined}>
      <LincNav pendingApprovals={pending} businesses={businesses} />
      {!lincWerksHome ? (
        <button className="app-brand-chip" onClick={() => navigate("/focus")} title="LincWerks home" aria-label="LincWerks home">
          <img src="/LincWerks2026Logo.png" alt="" />
          <span><b>LincWerks</b><small>Operating ecosystem</small></span>
        </button>
      ) : null}
      <main className="canvas" data-edit-name="Page canvas">
        <Outlet context={{ theme, setTheme }} />
      </main>
      {scope === "full" ? <GlobalPartner surface={location.pathname} onNavigate={navigate} /> : null}
      {scope === "full" ? (
        <AvatarOverlayLayer
          moduleId={resolveAvatarModule(location.pathname)}
          pathname={location.pathname}
          summons={lincWerksHome ? ["arti"] : []}
          onAsk={onAvatarAsk}
          onVoiceToggle={onAvatarVoiceToggle}
        />
      ) : null}
      <EditModeHost />
    </div>
  );
}
